import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDeals } from './useDeals.js';
import { useDealSummary } from './useDealSummary.js';
import { DealService } from '@vanilla/services/DealService.js';
import { permissions } from '@vanilla/services/PermissionService.js';
import { getStageLabel } from '@vanilla/constants/Stages.js';
import { KANBAN_SORT_OPTIONS } from '@vanilla/utils/KanbanDealSort.js';
import { getKanbanSortPref, saveKanbanSortPref, getDefaultDirForSortKey } from '@vanilla/constants/KanbanSortPrefs.js';
import { ToastManager } from '@/lib/toast.js';
import { buildDealPath } from '../shell/routeAccess.js';
import FilterPanel from './FilterPanel.jsx';
import DealsHeader from './DealsHeader.jsx';
import KanbanBoard from './KanbanBoard.jsx';
import TableView from './TableView.jsx';
import DisplaySettingsDialog from './DisplaySettingsDialog.jsx';
import ExportModal from './ExportModal.jsx';
import DealFormDialog from './DealFormDialog.jsx';
import { FullPageLoader } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@/components/ui/popover';

export default function DealsWorkspace() {
    const { dealId: routeDealId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [filterCollapsed, setFilterCollapsed] = useState(() => localStorage.getItem('filterPanelCollapsed') === 'true');
    const onToggleFilter = useCallback(() => setFilterCollapsed((c) => !c), []);

    useEffect(() => {
        localStorage.setItem('filterPanelCollapsed', String(filterCollapsed));
    }, [filterCollapsed]);

    const {
        deals,
        schedules,
        clients,
        users,
        scopedOwners,
        filters,
        periodLabel,
        loading,
        error,
        setFilter,
        resetFilters,
        clearFilter,
        applyOwnerScope,
        refresh,
    } = useDeals();

    const summary = useDealSummary(deals, filters, schedules);

    const [view, setView] = useState(() => searchParams.get('view') === 'table' ? 'table' : 'kanban');
    const [searchInput, setSearchInput] = useState(filters.search || '');
    const [kanbanVersion, setKanbanVersion] = useState(0);
    const [tableVersion, setTableVersion] = useState(0);
    const [formOpen, setFormOpen] = useState(false);
    const [formDealId, setFormDealId] = useState(null);
    const [formSidebarTab, setFormSidebarTab] = useState(null);
    const [formTimelineFilter, setFormTimelineFilter] = useState(null);
    const [formDrawerOpen, setFormDrawerOpen] = useState(false);
    const [formHighlight, setFormHighlight] = useState(null);
    const [exportOpen, setExportOpen] = useState(false);
    const [displayOpen, setDisplayOpen] = useState(false);
    const [sortPopover, setSortPopover] = useState(null);
    const [beyondOwn, setBeyondOwn] = useState(() => {
        const scope = permissions.getModuleScope('deals');
        return scope === 'team' || scope === 'all';
    });

    useEffect(() => {
        const t = setTimeout(() => {
            if ((filters.search || '') !== searchInput) {
                setFilter({ search: searchInput });
            }
        }, 280);
        return () => clearTimeout(t);
    }, [searchInput, setFilter]);

    useEffect(() => {
        setSearchInput(filters.search || '');
    }, [filters.search]);

    useEffect(() => {
        const next = view === 'table' ? 'table' : null;
        const current = searchParams.get('view');
        if (next && current !== next) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('view', next);
            setSearchParams(nextParams, { replace: true });
        } else if (!next && current) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('view');
            setSearchParams(nextParams, { replace: true });
        }
    }, [view]);

    const onScopeResolved = useCallback((scope) => {
        applyOwnerScope(scope);
        setBeyondOwn(!!scope?.beyondOwn);
    }, [applyOwnerScope]);

    const openCreate = useCallback(() => {
        setFormDealId(null);
        setFormSidebarTab(null);
        setFormTimelineFilter(null);
        setFormDrawerOpen(false);
        setFormHighlight(null);
        setFormOpen(true);
        if (routeDealId) {
            navigate({ pathname: '/deals', search: searchParams.toString() ? `?${searchParams.toString()}` : '' }, { replace: true });
        }
    }, [navigate, routeDealId, searchParams]);

    const openEdit = useCallback((dealId, options) => {
        const tab = options?.sidebarTab || null;
        const highlight = options?.highlightActivityId || options?.highlightCommentId || options?.highlightNextStepId || null;
        const filter = options?.timelineFilter || options?.drawerFilter || null;
        setFormDealId(dealId);
        setFormSidebarTab(tab);
        setFormTimelineFilter(filter);
        setFormDrawerOpen(!!options?.openDrawer || !!options?.timelineOpen);
        setFormHighlight(highlight);
        setFormOpen(true);
        const path = buildDealPath(dealId, {
            tab: tab === 'timeline' || tab === 'tasks' ? tab : undefined,
            highlight: highlight || undefined,
            filter: filter || undefined,
        });
        const [pathname, qs] = path.split('?');
        const kept = new URLSearchParams(searchParams);
        ['tab', 'highlight', 'filter', 'dealId', 'deal'].forEach((k) => kept.delete(k));
        if (qs) {
            new URLSearchParams(qs).forEach((v, k) => kept.set(k, v));
        }
        const search = kept.toString();
        navigate({ pathname, search: search ? `?${search}` : '' }, { replace: false });
    }, [navigate, searchParams]);

    const onSaved = useCallback(() => {
        setFormOpen(false);
        setFormDealId(null);
        setFormSidebarTab(null);
        setFormTimelineFilter(null);
        setFormDrawerOpen(false);
        setFormHighlight(null);
        const next = new URLSearchParams(searchParams);
        next.delete('tab');
        next.delete('highlight');
        next.delete('filter');
        next.delete('dealId');
        next.delete('deal');
        const search = next.toString();
        navigate({ pathname: '/deals', search: search ? `?${search}` : '' }, { replace: true });
    }, [navigate, searchParams]);

    const onMove = useCallback(async (dealId, stageKey) => {
        const moved = deals.find((d) => String(d.id) === String(dealId));
        const fromStage = moved?.dealStage;
        try {
            await DealService.updateStage(dealId, stageKey);
            ToastManager.success(
                'Deal moved',
                `${moved?.dealName || moved?.name || 'Deal'} moved from ${fromStage ? getStageLabel(fromStage) : 'previous stage'} to ${getStageLabel(stageKey)}.`,
            );
            refresh();
        } catch (err) {
            console.error('[DealsWorkspace] move failed', err);
            if (err.missingFields && err.dealId) {
                openEdit(err.dealId, {});
                if (err.intendedStage === 'closedLost') {
                    setTimeout(() => {
                        document.getElementById('dealLostReason')?.focus();
                        ToastManager.show({ title: 'Lost details required', message: 'Please fill Lost Reason and Lost Category to close as lost.', tone: 'warning' });
                    }, 600);
                } else {
                    ToastManager.show({
                        title: 'Missing required fields',
                        message: `Required to close: ${err.missingFields.join(', ')}.`,
                        tone: 'warning',
                    });
                }
            } else {
                ToastManager.crmError('Move', 'deal', err, 'Please verify required fields and permissions, then try again.');
                refresh();
            }
        }
    }, [deals, refresh, openEdit]);

    const onSort = useCallback((stageKey, eOrRect) => {
        const pref = getKanbanSortPref(stageKey);
        let rect = null;
        if (eOrRect && typeof eOrRect.getBoundingClientRect === 'function') {
            rect = eOrRect.getBoundingClientRect();
        } else if (eOrRect?.currentTarget?.getBoundingClientRect) {
            rect = eOrRect.currentTarget.getBoundingClientRect();
        } else if (eOrRect && typeof eOrRect.right === 'number' && typeof eOrRect.bottom === 'number') {
            rect = eOrRect;
        } else {
            const trigger = document.querySelector(`.kanban-settings-btn[data-stage="${stageKey}"]`);
            rect = trigger?.getBoundingClientRect() || null;
        }
        const left = rect ? Math.max(8, rect.right - 280) : 8;
        const top = rect ? rect.bottom + 6 : 8;
        setTimeout(() => setSortPopover({ stageKey, key: pref.key, dir: pref.dir, left, top }), 80);
    }, []);

    const applySort = useCallback(() => {
        if (!sortPopover) return;
        saveKanbanSortPref(sortPopover.stageKey, { key: sortPopover.key, dir: sortPopover.dir });
        setSortPopover(null);
        setKanbanVersion((v) => v + 1);
    }, [sortPopover]);
    useEffect(() => {
        const legacyId = searchParams.get('dealId') || searchParams.get('deal');
        if (legacyId && !routeDealId) {
            const highlight = searchParams.get('highlight') || searchParams.get('activity');
            const filter = searchParams.get('filter') || 'comments';
            const next = new URLSearchParams(searchParams);
            next.delete('dealId');
            next.delete('deal');
            const kept = next.toString();
            const path = buildDealPath(legacyId, {
                tab: 'timeline',
                highlight: highlight || undefined,
                filter,
            });
            const [pathname, qs] = path.split('?');
            const merged = new URLSearchParams(kept);
            if (qs) new URLSearchParams(qs).forEach((v, k) => merged.set(k, v));
            const search = merged.toString();
            navigate({ pathname, search: search ? `?${search}` : '' }, { replace: true });
            return;
        }

        if (routeDealId) {
            setFormDealId(routeDealId);
            setFormSidebarTab(searchParams.get('tab') || null);
            setFormTimelineFilter(searchParams.get('filter') || null);
            setFormDrawerOpen(!!searchParams.get('highlight') || searchParams.get('tab') === 'timeline');
            setFormHighlight(searchParams.get('highlight') || null);
            setFormOpen(true);
        }
    }, [routeDealId, searchParams, navigate]);

    useEffect(() => {
        const onOpenDealForm = (e) => {
            const d = e.detail || {};
            if (d.dealId) openEdit(d.dealId, d);
            else openCreate();
        };
        window.addEventListener('openDealForm', onOpenDealForm);
        return () => window.removeEventListener('openDealForm', onOpenDealForm);
    }, [openEdit, openCreate]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem('crm_pending_open_deal');
            if (raw) {
                const parsed = JSON.parse(raw);
                const detail = parsed?.detail;
                const ts = parsed?.ts || 0;
                if (detail?.dealId && Date.now() - ts < 30000) {
                    openEdit(detail.dealId, detail);
                }
                localStorage.removeItem('crm_pending_open_deal');
            }
        } catch {}
        const onStorage = (e) => {
            if (e.key === 'crm_pending_open_deal' && e.newValue) {
                try {
                    const parsed = JSON.parse(e.newValue);
                    if (parsed?.detail?.dealId) openEdit(parsed.detail.dealId, parsed.detail);
                } catch {}
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [openEdit]);

    return (
        <div className="app-shell flex flex-col min-h-0 flex-1">
            <DealsHeader
                view={view}
                onViewChange={setView}
                search={searchInput}
                onSearchChange={setSearchInput}
                summary={summary}
                onNewDeal={openCreate}
                onToggleFilter={onToggleFilter}
                filterCollapsed={filterCollapsed}
                filters={filters}
                periodLabel={periodLabel}
                onDisplaySettings={() => setDisplayOpen(true)}
                onExport={() => setExportOpen(true)}
            />
            <div className="app-body flex-1 min-h-0">
                <FilterPanel
                    filters={filters}
                    setFilter={setFilter}
                    users={users}
                    clients={clients}
                    open={!filterCollapsed}
                    onOpenChange={(nextOpen) => {
                        if (nextOpen === filterCollapsed) {
                            onToggleFilter();
                        }
                    }}
                    onScopeResolved={onScopeResolved}
                />

                <div className="main-panel flex-1 min-w-0">
                    <div className="dashboard-scroll flex-1">
                        {error && (
                            <div className="crm-error-banner" role="alert">
                                <span>{error}</span>
                                <Button type="button" className="btn btn-secondary btn-sm" variant="secondary" size="sm" onClick={() => refresh()}>Retry</Button>
                            </div>
                        )}
                        {loading && deals.length === 0 && (
                            <FullPageLoader message="Loading your deals..." />
                        )}
                        {view === 'kanban' ? (
                            <KanbanBoard deals={deals} onMove={onMove} onEdit={openEdit} onSort={onSort} version={kanbanVersion} />
                        ) : (
                            <TableView deals={deals} onEdit={openEdit} onRefresh={refresh} version={tableVersion} />
                        )}
                    </div>
                </div>
            </div>

            {sortPopover && (
                <Popover
                    open
                    onOpenChange={(next) => { if (!next) setSortPopover(null); }}
                >
                    <PopoverAnchor asChild>
                        <span
                            aria-hidden="true"
                            style={{
                                position: 'fixed',
                                left: sortPopover.left,
                                top: sortPopover.top,
                                width: 1,
                                height: 1,
                                pointerEvents: 'none',
                            }}
                        />
                    </PopoverAnchor>
                    <PopoverContent
                        unstyled
                        className="kanban-sort-popover"
                        align="start"
                        side="bottom"
                        sideOffset={0}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                        <div className="kanban-sort-popover-title">Sort column</div>
                        <label className="kanban-sort-field">
                            <span>Sort by</span>
                            <NativeSelect unstyled
                                className="form-input kanban-sort-key"
                                value={sortPopover.key}
                                onChange={(e) => {
                                    const k = e.target.value;
                                    setSortPopover((p) => ({ ...p, key: k, dir: getDefaultDirForSortKey(k) }));
                                }}
                            >
                                {KANBAN_SORT_OPTIONS.map((o) => (
                                    <option value={o.key} key={o.key}>{o.label}</option>
                                ))}
                            </NativeSelect>
                        </label>
                        <label className="kanban-sort-field">
                            <span>Order</span>
                            <NativeSelect unstyled
                                className="form-input kanban-sort-dir"
                                value={sortPopover.dir}
                                onChange={(e) => setSortPopover((p) => ({ ...p, dir: e.target.value }))}
                            >
                                <option value="desc">High → low / Newest first</option>
                                <option value="asc">Low → high / Oldest first</option>
                            </NativeSelect>
                        </label>
                        <div className="kanban-sort-popover-actions">
                            <Button type="button" className="btn btn-primary btn-sm" variant="default" size="sm" onClick={applySort}>Apply</Button>
                        </div>
                    </PopoverContent>
                </Popover>
            )}

            {exportOpen && <ExportModal deals={deals} onClose={() => setExportOpen(false)} />}
            {displayOpen && (
                <DisplaySettingsDialog
                    onClose={() => setDisplayOpen(false)}
                    onSaved={() => {
                        setTableVersion((v) => v + 1);
                        setKanbanVersion((v) => v + 1);
                    }}
                />
            )}
            {formOpen && (
                <DealFormDialog
                    dealId={formDealId}
                    sidebarTab={formSidebarTab}
                    timelineFilter={formTimelineFilter}
                    openTimelineDrawer={formDrawerOpen}
                    highlightActivityId={formHighlight}
                    onClose={onSaved}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
}
